
import Vue from "vue";
import { getBrandConfig } from "@/services/brandConfigService";
import GameMaterialModal from "@/components/management/GameMaterialModal.vue";
import CreateGameModal from "@/components/management/CreateGameModal.vue";
import SideBar from "@/components/SideBar.vue";
import { getCurrentUser } from "@/auth";
import { apiFetch } from "@/services/apiClient";
import { clearGameDataCache } from "@/services/gameDataService";
import {
  GameMaterials,
  GameEntry,
  GameSavePayload,
  ApiGame,
} from "@/components/management/types";

type ManagementStore = Record<string, GameMaterials>;

const DEFAULT_MATERIALS: GameMaterials = {
  demoLink: null,
  gdd: [],
  cad: [],
  marketing: [],
  latestArt: [],
  approvedMaterials: [],
};

export default Vue.extend({
  name: "ManagementView",

  components: {
    GameMaterialModal,
    CreateGameModal,
    SideBar,
  },

  data() {
    return {
      selectedBrandId: null as string | null,
      gameSearch: "" as string,
      allGames: [] as GameEntry[],
      apiGames: [] as ApiGame[],
      materials: {} as ManagementStore,
      modalGame: null as GameEntry | null,
      isSaving: false,
      isDeleting: false,
      showCreateModal: false,
      mobileMenuOpen: false,
      currentBrandId: getCurrentUser()?.brand || "",
      mobileMenuHandler: null as ((isOpen: boolean) => void) | null,
    };
  },

  computed: {
    brandOptions(): Array<{ id: string; name: string; filePath: string; brandId: string }> {
      try {
        const config = getBrandConfig('jogoglobal-admin');
        return config.categories.map((c: any) => ({
          id: c.name,
          name: c.name,
          filePath: c.gamesPath,
          brandId: config.id,
        }));
      } catch {
        return [];
      }
    },

    modalGameData(): Record<string, unknown> {
      if (!this.modalGame) return {};
      const apiGame = this.apiGames.find((g: ApiGame) => g.id === this.modalGame!.gameId);
      if (!apiGame?.data) return {};
      const { name, img, gameFiles, ...rest } = apiGame.data;
      void name; void img; void gameFiles;
      return rest;
    },

    filteredGames(): GameEntry[] {
      let games = this.allGames;
      if (this.selectedBrandId) {
        games = games.filter(g => g.categoryName === this.selectedBrandId);
      }
      const q = this.gameSearch.toLowerCase().trim();
      if (!q) return games;
      return games.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.categoryName.toLowerCase().includes(q)
      );
    },
  },

  mounted() {
    this.loadGames();
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.height = "";
    document.body.style.width = "";
    this.mobileMenuHandler = (isOpen: boolean) => {
      this.mobileMenuOpen = isOpen;
      document.body.style.overflow = isOpen ? "hidden" : "";
    };
    this.$root.$on("mobile-menu-toggle", this.mobileMenuHandler);
  },

  beforeDestroy() {
    if (this.mobileMenuHandler) {
      this.$root.$off("mobile-menu-toggle", this.mobileMenuHandler);
    }
  },

  methods: {
    closeMobileSidebar() {
      this.mobileMenuOpen = false;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.height = "";
      document.body.style.width = "";
    },

    onSidebarGameSelected() {
      this.$router.push("/dashboard");
      this.closeMobileSidebar();
    },

    onSidebarReset() {
      this.$router.push("/dashboard");
      this.closeMobileSidebar();
    },

    async loadGames() {
      try {
        const res = await apiFetch('/api/games');
        if (!res.ok) throw new Error(`Failed to load games (${res.status})`);
        this.apiGames = await res.json();

        const config = getBrandConfig('jogoglobal-admin');
        const entries: GameEntry[] = [];

        for (const category of config.categories) {
          const categoryGames = this.apiGames.filter((g: ApiGame) => {
            if (g.file_path !== category.gamesPath) return false;
            if (category.ipKeys?.length && g.ip_key) return category.ipKeys.includes(g.ip_key);
            return true;
          });

          for (const g of categoryGames) {
            const key = `jogoglobal-admin||${category.name}||${g.name}`;
            entries.push({
              key,
              name: g.name,
              categoryName: category.name,
              brandId: g.brand_id,
              gameId: g.id,
              filePath: g.file_path,
              thumbnail: g.data?.img || '',
            });
            this.seedMaterials(key, g.data.gameFiles || []);
          }
        }

        this.allGames = entries;
      } catch (e) {
        console.error('Failed to load games', e);
        this.$toast.error('Failed to load games. Please reload the page.');
      }
    },

    getMaterials(key: string): GameMaterials {
      const m = this.materials[key];
      if (!m) return { ...DEFAULT_MATERIALS };
      return {
        demoLink: m.demoLink,
        gdd: [...m.gdd],
        cad: [...m.cad],
        marketing: [...m.marketing],
        latestArt: [...m.latestArt],
        approvedMaterials: [...m.approvedMaterials],
      };
    },

    getCompletion(key: string): number {
      const m = this.getMaterials(key);
      let filled = 0;
      if (m.demoLink) filled++;
      if (m.gdd.length) filled++;
      if (m.cad.length) filled++;
      if (m.marketing.length) filled++;
      if (m.latestArt.length) filled++;
      if (m.approvedMaterials.length) filled++;
      return Math.round((filled / 6) * 100);
    },

    openModal(game: GameEntry) {
      this.modalGame = game;
    },

    async onSave(payload: GameSavePayload) {
      if (!this.modalGame) return;
      const game = this.modalGame;

      const apiGame = this.apiGames.find((g: ApiGame) => g.id === game.gameId);
      if (!apiGame) {
        this.modalGame = null;
        return;
      }

      const previousMaterials = this.materials[game.key];
      this.$set(this.materials, game.key, payload.materials);

      const updatedData = {
        ...payload.metadata.gameMeta,
        name: payload.metadata.name,
        img: payload.metadata.thumbnail,
        gameFiles: this.materialsToGameFiles(apiGame.data.gameFiles || [], payload.materials),
      };

      const body: any = { data: updatedData };
      const categoryChanged =
        payload.metadata.filePath !== game.filePath ||
        payload.metadata.categoryName !== game.categoryName;
      if (categoryChanged) {
        body.file_path = payload.metadata.filePath;
        body.brand_id = payload.metadata.brandId;
      }

      this.isSaving = true;
      try {
        const res = await apiFetch(`/api/games/${game.gameId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Server error (${res.status})`);

        apiGame.data = updatedData;
        if (categoryChanged) {
          apiGame.file_path = body.file_path;
          apiGame.brand_id = body.brand_id;
        }

        clearGameDataCache();

        const entry = this.allGames.find(g => g.key === game.key);
        if (entry) {
          entry.name = payload.metadata.name;
          entry.thumbnail = payload.metadata.thumbnail;
        }

        if (categoryChanged) {
          await this.loadGames();
        }

        this.modalGame = null;
        this.$toast.success(`"${payload.metadata.name}" saved successfully!`);
      } catch (e) {
        console.error('Failed to save game', e);
        this.$toast.error('Failed to save. Please try again.');
        if (previousMaterials !== undefined) {
          this.$set(this.materials, game.key, previousMaterials);
        } else {
          this.$delete(this.materials, game.key);
        }
      } finally {
        this.isSaving = false;
      }
    },

    async onGameCreated(newGame: ApiGame) {
      this.showCreateModal = false;
      await this.loadGames();
      this.$toast.success(`"${newGame.name}" created successfully!`);
    },

    async onGameDelete(gameId: number) {
      const game = this.modalGame;
      this.isDeleting = true;
      try {
        const res = await apiFetch(`/api/games/${gameId}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(`Server error (${res.status})`);

        this.allGames = this.allGames.filter(g => g.gameId !== gameId);
        this.apiGames = this.apiGames.filter((g: ApiGame) => g.id !== gameId);
        if (game) this.$delete(this.materials, game.key);
        this.modalGame = null;
        clearGameDataCache();
        this.$toast.success(`"${game?.name}" deleted successfully!`);
      } catch (e) {
        console.error('Failed to delete game', e);
        this.$toast.error('Failed to delete game. Please try again.');
      } finally {
        this.isDeleting = false;
      }
    },

    materialsToGameFiles(original: any[], updated: GameMaterials): any[] {
      const result = original.map((f: any) => ({ ...f }));

      const demoIdx = result.findIndex((f: any) =>
        (f.name || '').toLowerCase().includes('demo') || f.type === 'Playable'
      );
      if (updated.demoLink) {
        const url = updated.demoLink;
        if (demoIdx >= 0) {
          result[demoIdx] = { ...result[demoIdx], url, action: 'Play Here', actionType: 'api-launch', openDirectly: true };
        } else {
          result.unshift({ name: 'Demo Link', type: 'Playable', action: 'Play Here', actionType: 'api-launch', openDirectly: true, url });
        }
      } else if (demoIdx >= 0) {
        result[demoIdx] = { ...result[demoIdx], action: 'Coming Soon', actionType: 'disabled', url: '' };
      }

      const makeButton = (entry: any, url?: string, typeOverride?: string) => ({
        ...entry,
        action: 'Download',
        actionType: 'download',
        ...(url ? { url } : {}),
        ...(typeOverride ? { type: typeOverride } : {}),
      });
      const pushNew = (mat: { name: string; type: string; url?: string }, displayType = 'Information document', nameOverride?: string) => ({
        name: nameOverride || mat.name,
        type: displayType,
        action: 'Download',
        actionType: 'download',
        ...(mat.url ? { url: mat.url } : {}),
      });

      const applyCategory = (
        items: typeof updated.gdd,
        matchFn: (f: any) => boolean,
        displayType: string,
        nameOverride: string,
        typeOverride?: string,
      ) => {
        const firstIdx = result.findIndex(matchFn);
        if (items.length > 0) {
          if (firstIdx >= 0) {
            result[firstIdx] = makeButton(result[firstIdx], items[0].url, typeOverride);
          } else {
            result.push(pushNew(items[0], displayType, nameOverride));
          }
          // Note: the modal constrains each category to at most 1 item via slice(-1),
          // so this loop is defensive. If that constraint is ever removed, extras
          // would be pushed here and then silently dropped by the dedup pass below.
          for (let i = 1; i < items.length; i++) {
            result.push(pushNew(items[i], displayType, items[i].name));
          }
        } else if (firstIdx >= 0) {
          result[firstIdx] = { ...result[firstIdx], actionType: 'disabled' };
        }
        // Remove all extra duplicate matches from original, keeping only the first.
        // keptIdx may be -1 if no match exists (items empty + no original entry);
        // in that case matchFn returns false for everything and the loop is a no-op.
        const keptIdx = result.findIndex(matchFn);
        for (let i = result.length - 1; i >= 0; i--) {
          if (i !== keptIdx && matchFn(result[i])) {
            result.splice(i, 1);
          }
        }
      };

      applyCategory(
        updated.gdd,
        (f: any) => { const n = (f.name || '').toLowerCase(); return n.includes('design document') || n.includes('gdd'); },
        'Information document', 'Game Design Document',
      );
      applyCategory(
        updated.cad,
        (f: any) => { const n = (f.name || '').toLowerCase(); return n.includes('concept approval') || n.includes('cad'); },
        'Information document', 'Concept Approval Document',
      );
      applyCategory(
        updated.marketing,
        (f: any) => { const n = (f.name || '').toLowerCase(); return n.includes('marketing') || n.includes('slick') || n.includes('project update') || n.includes('project summary'); },
        'Marketing Materials', 'Marketing Materials', 'Marketing Materials',
      );
      applyCategory(
        updated.latestArt,
        (f: any) => (f.name || '').toLowerCase().includes('latest art'),
        'Information document', 'Latest Art',
      );
      applyCategory(
        updated.approvedMaterials,
        (f: any) => (f.name || '').toLowerCase().includes('approved materials'),
        'Approved Materials', 'Approved Materials', 'Approved Materials',
      );

      // Remove orphaned LINK entries: legacy name='Link' stubs and any disabled LINK entries
      // Also remove orphaned download/button entries without URL or gameId (e.g. files uploaded without URL)
      return result.filter((f: any) => {
        if (f.type !== 'LINK') {
          if ((f.actionType === 'button' || f.actionType === 'download') && !f.url && !f.gameId) return false;
          return true;
        }
        if ((f.name || '').toLowerCase() === 'link') return false;
        if (f.actionType === 'disabled') return false;
        return true;
      });
    },

    seedMaterials(key: string, gameFiles: any[]): void {
      if (!gameFiles.length || this.materials[key]) return;

      const result: GameMaterials = { demoLink: null, gdd: [], cad: [], marketing: [], latestArt: [], approvedMaterials: [] };

      for (const f of gameFiles) {
        const n = (f.name || '').toLowerCase();
        const hasContent = f.actionType !== 'disabled' && (f.url || f.gameId);
        if (!hasContent) continue;

        const url = f.url || undefined;
        const ext = f.url ? (f.url.split('?')[0].split('.').pop()?.toUpperCase() || 'FILE') : 'FILE';
        if (n.includes('demo') || (f.type === 'Playable' && !result.demoLink)) {
          result.demoLink = f.url || `[api-launch: ${f.gameId}]`;
        } else if (n.includes('design document') || n.includes('gdd')) {
          result.gdd.push({ name: f.name, type: 'PDF', url });
        } else if (n.includes('concept approval') || n.includes('cad')) {
          result.cad.push({ name: f.name, type: 'PDF', url });
        } else if (n.includes('latest art')) {
          result.latestArt.push({ name: f.name, type: ext, url });
        } else if (n.includes('approved materials')) {
          result.approvedMaterials.push({ name: f.name, type: ext, url });
        } else if (n.includes('marketing') || n.includes('slick') ||
          n.includes('project update') || n.includes('project summary')) {
          result.marketing.push({ name: f.name, type: ext, url });
        }
      }

      if (result.demoLink || result.gdd.length || result.cad.length || result.marketing.length || result.latestArt.length || result.approvedMaterials.length) {
        this.$set(this.materials, key, result);
      }
    },
  },
});
